/**
 * feat-107 shared primitive (3/4) — Commit orchestration helper (ADR 0022).
 *
 * Writes an importer plan (or any future bulk action) into the curriculum
 * through the EXISTING per-item REST APIs — NEVER the DB directly — so the
 * feat-018 auto-translation pipeline fires on every created item. Owns:
 *   1. target-Lesson resolution (existing `unitId`, or `createUnit()` for a new one),
 *   2. per-Drill `createLesson` → per-child `createQuestion`/`createRehearsal`/
 *      `createReadingItem` (feat-343),
 *      strictly SEQUENTIAL (truthful progress; no translation thundering),
 *   3. committed-id tracking so partial-failure Retry re-issues ONLY the failed
 *      writes and never duplicates already-saved items,
 *   4. a per-item progress callback the wizard renders live,
 *   5. a failed Drill SKIPS its children (status 'skipped') with a clear note.
 *
 * The SIGNATURE + progress/state/result types are FINAL (this ADR). Slice B
 * builds the `CommitPlan` (mapping ParsedItems → payloads) and renders progress;
 * it does not change this contract. The body is a thin real implementation.
 *
 * Translatable-field note (feat-018, spec §Translation contract): CREATE always
 * enqueues translation, so every item here queues translation by construction —
 * `translationQueued` is set true on each successful create. (The UPDATE-only
 * `_touches_translatable()` gate is an EDITOR concern, not the importer's.)
 */

import {
  createUnit,
  createLesson,
  createQuestion,
  createRehearsal,
  createReadingItem,
} from './quizContentAdminService';

// Payload types are pinned to the live service signatures so they can't drift.
export type CreateUnitPayload = Parameters<typeof createUnit>[1];
export type CreateLessonPayload = Parameters<typeof createLesson>[1];
export type CreateQuestionPayload = Parameters<typeof createQuestion>[1];
export type CreateRehearsalPayload = Parameters<typeof createRehearsal>[1];
/** feat-343: the Reading create body MINUS `tenant_id`, which `commitCurriculum`
 *  injects from its own `tenantId` argument (the plan builder has no tenant).
 *  No `image_s3_keys` — the importer never imports images. */
export type CreateReadingPayload = Omit<
  Parameters<typeof createReadingItem>[1],
  'tenant_id' | 'image_s3_keys'
>;

// ============================================================
// Plan (input) — caller builds this from the reviewed hierarchy
// ============================================================

/** How to resolve the lvl-1 Lesson everything imports into. Exactly one of the
 *  two is set: an already-existing unit, or a new unit to create first. */
export type CommitTarget =
  | { kind: 'existing'; unitId: string }
  | { kind: 'new'; payload: CreateUnitPayload };

export type CommitChildInput =
  | { tempId: string; kind: 'question'; label: string; payload: CreateQuestionPayload }
  | { tempId: string; kind: 'rehearsal'; label: string; payload: CreateRehearsalPayload }
  | { tempId: string; kind: 'reading'; label: string; payload: CreateReadingPayload };

export interface CommitDrillInput {
  tempId: string;
  label: string;
  payload: CreateLessonPayload;
  children: CommitChildInput[];
}

export interface CommitPlan {
  target: CommitTarget;
  drills: CommitDrillInput[];
}

// ============================================================
// Progress + committed-id state (live; rendered by the wizard)
// ============================================================

export type CommitItemStatus = 'queued' | 'saving' | 'saved' | 'failed' | 'skipped';
export type CommitNodeKind = 'unit' | 'drill' | 'question' | 'rehearsal' | 'reading';

export interface CommitItemProgress {
  tempId: string;
  kind: CommitNodeKind;
  label: string;
  status: CommitItemStatus;
  /** Set once saved — the committed id (unitId / lessonId / questionId / rehearsalId). */
  savedId?: string;
  /** True on a successful create (feat-018 enqueued translation). */
  translationQueued?: boolean;
  /** Set on failure: HTTP status (when known) + a human-facing reason. */
  error?: { status?: number; message: string };
}

export type CommitProgressCallback = (snapshot: CommitItemProgress[]) => void;

/**
 * Committed-id ledger — pass the prior run's `state` back into `commitCurriculum`
 * on Retry. Anything already in these maps is SKIPPED (not re-created), so retry
 * is idempotent and never duplicates.
 */
export interface CommitState {
  /** Resolved target unit id (set after a 'new' unit is created, or echoed for 'existing'). */
  unitId?: string;
  /** drill tempId → created lessonId. */
  drillIds: Record<string, string>;
  /** child tempId → created question/rehearsal id. */
  itemIds: Record<string, string>;
  /** Latest per-node progress snapshot (drives the UI on resume). */
  progress: CommitItemProgress[];
}

export interface CommitResult {
  state: CommitState;
  succeeded: number;
  failed: number;
  /** tempIds of children/drills that failed (or were skipped) — feed Retry. */
  failedTempIds: string[];
}

export function emptyCommitState(): CommitState {
  return { drillIds: {}, itemIds: {}, progress: [] };
}

// ============================================================
// Orchestrator
// ============================================================

/**
 * Run (or resume) a commit. Sequential by item; emits a fresh progress snapshot
 * to `onProgress` after every state change. Pass `prior` (a previous result's
 * `state`) to retry: already-committed unit/drill/item ids are reused and their
 * writes skipped. Resolves (never rejects) with a `CommitResult` — per-item
 * failures are reported in `state.progress`, not thrown.
 */
export async function commitCurriculum(
  tenantId: string,
  plan: CommitPlan,
  onProgress: CommitProgressCallback,
  prior: CommitState = emptyCommitState(),
): Promise<CommitResult> {
  const state: CommitState = {
    unitId: prior.unitId,
    drillIds: { ...prior.drillIds },
    itemIds: { ...prior.itemIds },
    progress: [],
  };

  // Build the ordered progress ledger (unit → per drill → its children).
  const progress: CommitItemProgress[] = [];
  const byTempId = new Map<string, CommitItemProgress>();
  const push = (p: CommitItemProgress) => {
    progress.push(p);
    byTempId.set(p.tempId, p);
  };

  const UNIT_TEMP = '__unit__';
  if (plan.target.kind === 'new') {
    push({
      tempId: UNIT_TEMP,
      kind: 'unit',
      label: plan.target.payload.title,
      status: state.unitId ? 'saved' : 'queued',
      savedId: state.unitId,
      translationQueued: state.unitId ? true : undefined,
    });
  }
  for (const drill of plan.drills) {
    const drillId = state.drillIds[drill.tempId];
    push({
      tempId: drill.tempId,
      kind: 'drill',
      label: drill.label,
      status: drillId ? 'saved' : 'queued',
      savedId: drillId,
      translationQueued: drillId ? true : undefined,
    });
    for (const child of drill.children) {
      const childId = state.itemIds[child.tempId];
      push({
        tempId: child.tempId,
        kind: child.kind,
        label: child.label,
        status: childId ? 'saved' : 'queued',
        savedId: childId,
        translationQueued: childId ? true : undefined,
      });
    }
  }
  state.progress = progress;

  const emit = () => onProgress(progress.map((p) => ({ ...p })));
  const mark = (tempId: string, patch: Partial<CommitItemProgress>) => {
    const p = byTempId.get(tempId);
    if (p) Object.assign(p, patch);
    emit();
  };

  let succeeded = 0;
  let failed = 0;
  const failedTempIds: string[] = [];
  const fail = (tempId: string, err: unknown) => {
    failed += 1;
    failedTempIds.push(tempId);
    mark(tempId, { status: 'failed', error: toCommitError(err) });
  };

  emit();

  // 1) Resolve the target Lesson (unit).
  let unitId = state.unitId;
  if (!unitId) {
    if (plan.target.kind === 'existing') {
      unitId = plan.target.unitId;
      state.unitId = unitId;
    } else {
      mark(UNIT_TEMP, { status: 'saving' });
      try {
        const { id } = await createUnit(tenantId, plan.target.payload);
        unitId = id;
        state.unitId = id;
        succeeded += 1;
        mark(UNIT_TEMP, { status: 'saved', savedId: id, translationQueued: true });
      } catch (err) {
        // Target unit is the root — without it nothing else can be written.
        fail(UNIT_TEMP, err);
        for (const drill of plan.drills) {
          mark(drill.tempId, { status: 'skipped' });
          for (const child of drill.children) mark(child.tempId, { status: 'skipped' });
        }
        return { state, succeeded, failed, failedTempIds };
      }
    }
  }

  // 2) Per Drill → 3) per child. Sequential. A failed Drill skips its children.
  for (const drill of plan.drills) {
    let lessonId = state.drillIds[drill.tempId];
    if (!lessonId) {
      mark(drill.tempId, { status: 'saving' });
      try {
        const { id } = await createLesson(unitId, drill.payload);
        lessonId = id;
        state.drillIds[drill.tempId] = id;
        succeeded += 1;
        mark(drill.tempId, { status: 'saved', savedId: id, translationQueued: true });
      } catch (err) {
        fail(drill.tempId, err);
        for (const child of drill.children) {
          if (!state.itemIds[child.tempId]) mark(child.tempId, { status: 'skipped' });
        }
        continue; // skip this Drill's children
      }
    }

    for (const child of drill.children) {
      if (state.itemIds[child.tempId]) continue; // already saved on a prior run
      mark(child.tempId, { status: 'saving' });
      try {
        let id: string;
        if (child.kind === 'question') {
          id = (await createQuestion(lessonId, child.payload)).id;
        } else if (child.kind === 'rehearsal') {
          id = (await createRehearsal(lessonId, child.payload)).id;
        } else {
          // feat-343: Readings carry tenant_id in the body (the endpoint is
          // tenant-scoped by payload, unlike questions/rehearsals).
          id = (await createReadingItem(lessonId, { tenant_id: tenantId, ...child.payload })).id;
        }
        state.itemIds[child.tempId] = id;
        succeeded += 1;
        mark(child.tempId, { status: 'saved', savedId: id, translationQueued: true });
      } catch (err) {
        fail(child.tempId, err);
      }
    }
  }

  return { state, succeeded, failed, failedTempIds };
}

/** Normalize a thrown API error into `{status?, message}` for the progress row. */
function toCommitError(err: unknown): { status?: number; message: string } {
  if (err && typeof err === 'object') {
    const anyErr = err as { status?: number; statusCode?: number; message?: string };
    const status = anyErr.status ?? anyErr.statusCode;
    return { status, message: anyErr.message ?? String(err) };
  }
  return { message: String(err) };
}
